// VoiceRequestBanner — Jun 2026 (Randy). Shows at the top of the Creator
// Dashboard when visitors have asked a creator to turn ON voice (speech-to-
// speech) for a silo that isn't set up yet. Requests are STACKED server-side
// into one row per silo with a deduped count, so this is "N people want voice
// on [silo]" — not a flood. The row auto-disappears once the creator sets voice
// up (adds an OpenAI key or funds the OpenAI voucher).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, Key, Coins } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function VoiceRequestBanner() {
  const [rows, setRows] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/me/voice-requests");
        if (mounted) setRows(r.data.requests || []);
      } catch {
        /* opportunistic — silent */
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const visible = rows.filter((r) => !dismissed.has(r.silo_id));
  if (visible.length === 0) return null;

  const handleDismiss = async (siloId) => {
    setDismissed((s) => new Set(s).add(siloId));
    try {
      await api.post("/me/voice-requests/dismiss", { silo_id: siloId });
    } catch {
      setDismissed((s) => { const n = new Set(s); n.delete(siloId); return n; });
    }
  };

  return (
    <div className="space-y-2 mb-4" data-testid="voice-request-banner-stack">
      {visible.map((r) => (
        <div
          key={r.silo_id}
          className="border-2 border-[#4A5D4E] bg-[#EEF3EE] rounded-lg p-4 flex gap-3 items-start"
          data-testid={`voice-request-banner-${r.silo_id}`}
        >
          <div className="text-[#2F6B3A] flex-shrink-0 mt-0.5"><Microphone size={22} weight="fill" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#234A2E]" data-testid={`voice-request-title-${r.silo_id}`}>
              {r.count === 1 ? "Someone wants" : `${r.count} people want`} voice on “{r.silo_name}”
            </div>
            <div className="text-xs text-[#3A5240] mt-1 leading-relaxed">
              Visitors asked you to turn on the <strong>speech‑to‑speech</strong> feature so they can
              talk with your silo out loud. To switch it on, either add your own <strong>OpenAI key</strong>{" "}
              (you pay OpenAI direct, cheapest) or top up the silo's <strong>OpenAI voucher</strong>{" "}
              (runs on My Silo's key at the 3× platform rate). It turns on automatically once either is in place.
            </div>
            <div className="mt-2 flex gap-2 flex-wrap items-center">
              <button
                onClick={() => nav("/account-settings?focus=byok")}
                className="text-xs px-3 py-1.5 bg-[#234A2E] text-white rounded font-semibold hover:bg-[#173420] inline-flex items-center gap-1"
                data-testid={`voice-request-add-key-${r.silo_id}`}
              >
                <Key size={12} weight="bold" /> Add my OpenAI key →
              </button>
              <button
                onClick={() => nav(`/dashboard?silo=${r.slug}&tab=operations`)}
                className="text-xs px-3 py-1.5 bg-white border border-[#4A5D4E] text-[#234A2E] rounded font-semibold hover:bg-[#EEF3EE] inline-flex items-center gap-1"
                data-testid={`voice-request-voucher-${r.silo_id}`}
              >
                <Coins size={12} weight="bold" /> Top up OpenAI voucher (3×) →
              </button>
              <button
                onClick={() => handleDismiss(r.silo_id)}
                className="text-xs px-3 py-1.5 text-[#234A2E] rounded hover:bg-[#EEF3EE]"
                data-testid={`voice-request-dismiss-${r.silo_id}`}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
